import { useCallback, useEffect, useMemo, useState } from "react"
import { MRT_ColumnFiltersState, MRT_FullScreenToggleButton } from "material-react-table"
import type { MRT_ColumnDef } from "material-react-table"
import {
    Alert,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Snackbar,
    Tooltip,
    Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { DateTime } from "luxon"
import { Receipt, ReceiptDialog } from "../../../receipt/pages/ReceiptDialog"
import { KitchenTableOrder, fetchKitchenPurchasesByPeriod } from "../../controlKitchenApi"
import { useDateTime } from "../../../localization/useDateTime"
import { DropdownExportButtonControlKitchen } from "../components/DropdownExportButtonControlKitchen"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRotateRight, faFileChartColumn } from "@fortawesome/pro-light-svg-icons"
import { Box } from "@mui/system"
import { LocalizedEnum, LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import { makeStyles } from "tss-react/mui"
import { faClock } from "@fortawesome/pro-light-svg-icons"
import { createEnumChecker, iterateStringEnum } from "../../../../lib/enums"
import { UnreachableCaseError } from "../../../../lib/lang"
import { PageHeader } from "../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../shared/components/pageHeader/PageHeaderApi"
import { SelectControlKitchenMode } from "../components/SelectControlKitchenMode"
import { LocalCache, StorageKey } from "../../../../shared/cache/LocalCache"
import { StandardTimePicker } from "../../../../shared/components/pickers/StandardTimePicker"
import BooleanBadge from "../../../../shared/components/badges/BooleanBadge"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../shared/components/links/StandardBreadcrumbs"
import { MERCHANT_REPORTS_ROUTE } from "../../../../constants/routes"
import { useMerchant } from "../../../merchants/merchantHooks"
import { StandardToggleGlobalFilterButton } from "../../../../shared/components/table/StandardToggleGlobalFilterButton"
import { ApiQueryTable } from "../../../../core/api/components/ApiQueryTable"
import { useColumnFactory } from "../../../../core/api/useTableColumns"
import { type DtDateRange, StandardDateRangeFilter } from "../../../../shared/components/table/StandardDateRangeFilter"

enum CheckInFilter {
    ALL = "ALL",
    CHECKED_IN = "CHECKED_IN",
    NOT_CHECKED_IN = "NOT_CHECKED_IN",
}

const isCheckInFilter = createEnumChecker(CheckInFilter)

const useStyles = makeStyles()((theme) => ({
    controlCodeBox: {
        display: "flex",
        flexDirection: "column",
        width: "12rem",
        padding: "0.7rem",
        borderRadius: theme.shape.borderRadius * 2,
        backgroundColor: "#33c9dc",
    },
    dateFilter: {
        width: "auto",
        maxWidth: "10rem",
    },
    timeFilter: {
        width: "auto",
        maxWidth: "8rem",
    },
    checkInStatusFilter: {
        minWidth: "8.5rem",
    },
    customerName: {
        inlineSize: "8rem",
        overflowWrap: "break-word",
    },
    checkedInStatus: {
        borderRadius: theme.shape.borderRadius * 2,
    },
    totalItems: {
        marginRight: theme.spacing(0.5),
        padding: theme.spacing(0.25, 0.5),
        border: `1px solid ${theme.palette.text.secondary}`,
        borderRadius: theme.shape.borderRadius,
    },
    timePickerIconButton: {
        "& .MuiSvgIcon-root": {
            fontSize: "1.3rem",
        },
    },
}))

export type ControlKitchenMode = "DELIVERY_TIME" | "PURCHASE_TIME" | "PERIOD_REPORT"

const CHECK_IN_ID = "checked-in"
const PURCHASE_TIME_ID = "purchase-time-id"

export function ControlKitchen() {
    const { classes } = useStyles()

    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const [startDate, setStartDate] = useState<DateTime>(DateTime.now())
    const [receipt, setReceipt] = useState<Receipt>()

    const [fromDateTime, setFromDateTime] = useState<DateTime>(startDate.minus({ minute: 5 }))
    const [toDateTime, setToDateTime] = useState<DateTime>(startDate.plus({ minute: 15 }))

    // For period reporting
    const [periodDateRange, setPeriodDateRange] = useState<DtDateRange>({
        startDate: DateTime.now().startOf("day"),
        endDate: DateTime.now().endOf("day"),
    })

    const [checkInFilter, setCheckInFilter] = useState(CheckInFilter.ALL)

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])

    const [showErrorToaster, setShowErrorToaster] = useState(false)
    const [errorMessage, setErrorMessage] = useState("Something went wrong")

    const [mode, setMode] = useState<ControlKitchenMode>(
        (LocalCache().get(StorageKey.CONTROL_KITCHEN_MODE) as ControlKitchenMode) ?? "DELIVERY_TIME",
    )

    const { merchantId } = useMerchant()

    const isPeriodReportMode = mode === "PERIOD_REPORT"

    const setAndSaveMode = useCallback(
        (mode: ControlKitchenMode) => {
            setMode(mode)
            LocalCache().set(StorageKey.CONTROL_KITCHEN_MODE, mode)
        },
        [mode],
    )

    function handleChangeCheckInFilter(e: SelectChangeEvent<CheckInFilter>) {
        const value = e.target.value
        if (isCheckInFilter(value)) {
            setCheckInFilter(value)
        }
    }

    function showReceipt(row: KitchenTableOrder) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const lastUpdatedTime = () => {
        const timeInMS = queryResult.dataUpdatedAt
        const lastUpdated = dateTimeFactory.fromMillis(timeInMS)
        return dateTimeFactory.formatTimeHms(lastUpdated)
    }

    const queryResult = useApiQuery(fetchKitchenPurchasesByPeriod, {
        queryName: "kitchen-purchases-by-period",
        dependencies: {
            merchantId,
            startTime: isPeriodReportMode
                ? dateTimeFactory.toApiISOMinutesFormat(periodDateRange.startDate)
                : dateTimeFactory.toApiISOMinutesFormat(fromDateTime),
            endTime: isPeriodReportMode
                ? dateTimeFactory.toApiISOMinutesFormat(periodDateRange.endDate)
                : dateTimeFactory.toApiISOMinutesFormat(toDateTime),
            mode: mode,
        },
        enabled: isPeriodReportMode ? true : fromDateTime <= toDateTime,
    })

    const columnFactory = useColumnFactory<KitchenTableOrder>()

    const columns = useMemo<MRT_ColumnDef<KitchenTableOrder>[]>(() => {
        let res: MRT_ColumnDef<KitchenTableOrder>[] = [
            columnFactory.shortNameColumn({
                header: "Købstid",
                accessorKey: "created",
                id: PURCHASE_TIME_ID,
                Cell: ({ row }) => {
                    return <span>{dateTimeFactory.formatTime(dateTimeFactory.fromApi(row.original.created))}</span>
                },
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("control-kitchen-column-id"),
                accessorKey: "customer.employeeId",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("control-kitchen-column-name"),
                accessorKey: "customer.displayName",
                Cell: ({ row }) => {
                    return <p className={classes.customerName}>{row.original?.customer?.displayName}</p>
                },
            }),
            columnFactory.statusColumn({
                header: l10n.getString("control-kitchen-checked-in"),
                id: CHECK_IN_ID,
                accessorFn: (originalRow) => (originalRow?.receipt?.checkIn.isValid ? "yes" : "no"), // must be a string
                Cell: ({ row }) => {
                    const value = row.original?.receipt?.checkIn.isValid

                    return (
                        <BooleanBadge
                            isTrue={value}
                            labelTrue={l10n.getString("control-kitchen-checked-in-yes-chip")}
                            labelFalse={l10n.getString("control-kitchen-checked-in-no-chip")}
                        />
                    )
                },
                filterSelectOptions: [
                    {
                        value: "yes",
                        text: l10n.getString("control-kitchen-checked-in-filter-checked-in"),
                    },
                    {
                        value: "no",
                        text: l10n.getString("control-kitchen-checked-in-filter-not-checked-in"),
                    },
                ],
            }),
            columnFactory.nameColumn({
                header: l10n.getString("control-kitchen-column-order-details"),
                accessorKey: "orderLineNames",
                Cell: ({ row }) => {
                    return (
                        <>
                            <Typography variant="caption" color="text.secondary" className={classes.totalItems}>
                                {row.original?.orderSummary?.numberOfItems}
                            </Typography>
                            {row.original.orderLineNames}
                            <br />
                            {mode != "PURCHASE_TIME" && (
                                <Typography fontSize="0.6rem" variant="caption" color="text.secondary">
                                    {" "}
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        style={{ color: "#818283", paddingRight: "0.1rem", marginTop: "0.5rem" }}
                                    />{" "}
                                    {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(row.original.created))}{" "}
                                </Typography>
                            )}
                        </>
                    )
                },
            }),
            columnFactory.moneyColumn(
                { header: l10n.getString("office-purchase-amount"), id: "amount" },
                (row) => row.amount,
                true,
            ),
        ]

        // if (mode === "PURCHASE_TIME") {
        //     return res.filter((el) => {
        //         return el.id !== CHECK_IN_ID
        //     })
        // }
        // if (mode === "DELIVERY_TIME") {
        //     return res.filter((el) => {
        //         return el.id !== PURCHASE_TIME_ID
        //     })
        // }

        return res
    }, [l10n, classes, mode, columnFactory])

    const tableData = useMemo(() => {
        try {
            if (queryResult.data) {
                const data = queryResult.data
                return data.orders.map((order) => ({
                    ...order,
                    startDate: data.reportDetails.startDate,
                    endDate: data.reportDetails.endDate,
                    orderLineNames: order.orderLines?.map((l) => l.name).join(" \u2218 ") ?? "",
                }))
            }
            return []
        } catch (error: any) {
            setErrorMessage(error.displayMessage)
            setShowErrorToaster(true)
            return []
        }
    }, [queryResult.data])

    useEffect(() => {
        if (startDate !== null) {
            setFromDateTime((old) => startDate.set({ hour: old.hour, minute: old.minute, second: 0 }))
            setToDateTime((old) => startDate.set({ hour: old.hour, minute: old.minute, second: 0 }))
        }
    }, [startDate])

    useEffect(() => {
        if (fromDateTime > toDateTime) {
            setToDateTime(fromDateTime.plus({ minutes: 15 }))
        }
    }, [startDate, toDateTime, fromDateTime])

    useEffect(() => {
        const filter = columnFilters.find((filter) => filter.id === CHECK_IN_ID)

        setCheckInFilter((_prev) => {
            switch (filter?.value) {
                case "yes":
                    return CheckInFilter.CHECKED_IN
                case "no":
                    return CheckInFilter.NOT_CHECKED_IN
                case undefined:
                    return CheckInFilter.ALL
                default:
                    throw new Error(`Unknown check-in filter value: ${filter?.value}`)
            }
        })
    }, [columnFilters, setCheckInFilter])

    useEffect(() => {
        setColumnFilters((prev) => {
            const filter = prev.find((filter) => filter.id === CHECK_IN_ID)

            switch (checkInFilter) {
                case CheckInFilter.ALL:
                    if (filter) return prev.filter((f) => f.id !== CHECK_IN_ID)
                    else return prev
                case CheckInFilter.CHECKED_IN:
                    if (!filter || filter.value !== "yes") {
                        return [...prev.filter((f) => f.id !== CHECK_IN_ID), { id: CHECK_IN_ID, value: "yes" }]
                    } else {
                        return prev
                    }
                case CheckInFilter.NOT_CHECKED_IN:
                    if (!filter || filter.value !== "no") {
                        return [...prev.filter((f) => f.id !== CHECK_IN_ID), { id: CHECK_IN_ID, value: "no" }]
                    } else {
                        return prev
                    }
                default:
                    throw new UnreachableCaseError(checkInFilter)
            }
        })
    }, [checkInFilter, setColumnFilters])

    return (
        <>
            <Snackbar
                open={showErrorToaster}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                autoHideDuration={5000}
                onClose={() => setShowErrorToaster(false)}
            >
                <Alert
                    onClose={() => setShowErrorToaster(false)}
                    sx={{ marginBottom: "2rem", marginRight: "1rem" }}
                    severity="error"
                    variant="filled"
                >
                    {errorMessage}
                </Alert>
            </Snackbar>

            <Grid marginTop="3rem">
                <PageHeader
                    title={l10n.getString("merchant-nav-item-purchase-control")}
                    page={Page.PURCHASE_CONTROL}
                    icon={
                        <FontAwesomeIcon icon={faFileChartColumn} style={{ color: "#2d9cf3", paddingRight: "1rem" }} />
                    }
                    rightElement={
                        <Box className={classes.controlCodeBox}>
                            <LocalizedStrict id="control-kitchen-checked-in-sub-title">
                                <Typography variant="caption" color="white" fontSize="0.8rem" textAlign={"center"}>
                                    Todays control code:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="h6" alignSelf="end" color="white" sx={{ alignSelf: "center" }}>
                                {`${queryResult.data?.receipt.controlCode ?? ""}`}
                            </Typography>
                        </Box>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-purchase-control">
                            <Breadcrumb>Purchase Control</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>

                <ApiQueryTable
                    columns={columns}
                    onColumnFiltersChange={setColumnFilters}
                    data={tableData}
                    enableRowSelection
                    enableClickToCopy={false}
                    enableColumnOrdering
                    enableStickyHeader
                    enableStickyFooter
                    enableDensityToggle={false}
                    enableHiding
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <MenuItem
                            key={`receipt-${row.original.id}`}
                            onClick={() => {
                                showReceipt(row.original)
                                closeMenu()
                            }}
                        >
                            <LocalizedStrict id="control-kitchen-receipt">
                                <span>Receipt</span>
                            </LocalizedStrict>
                        </MenuItem>,
                    ]}
                    renderToolbarInternalActions={() => <></>}
                    renderTopToolbarCustomActions={({ table }) => (
                        <Grid container wrap="nowrap" direction="column" marginX="1rem">
                            <Grid item paddingBottom={"2rem"} width="100%">
                                <SelectControlKitchenMode mode={mode} setAndSaveMode={setAndSaveMode} className="" />
                            </Grid>

                            <Grid
                                container
                                wrap="nowrap"
                                justifyContent={"space-between"}
                                alignItems="baseline"
                                paddingBottom="1rem"
                            >
                                <Grid item container wrap="wrap" width="auto" gap="1rem">
                                    {isPeriodReportMode ? (
                                        <StandardDateRangeFilter
                                            value={periodDateRange}
                                            onChange={setPeriodDateRange}
                                            label={l10n.getString("control-kitchen-period-label")}
                                        />
                                    ) : (
                                        <>
                                            <DatePicker
                                                value={startDate}
                                                onChange={(newValue) => {
                                                    if (newValue) {
                                                        setStartDate(newValue)
                                                    }
                                                }}
                                                label={l10n.getString(
                                                    mode === "DELIVERY_TIME"
                                                        ? "control-kitchen-date-delivery-label"
                                                        : "control-kitchen-date-label",
                                                )}
                                                className={classes.dateFilter}
                                            />

                                            <StandardTimePicker
                                                label={l10n.getString("control-kitchen-datetime-from-label")}
                                                dateTime={fromDateTime}
                                                setDateTime={setFromDateTime}
                                                isMinutesStep={false}
                                            />

                                            <StandardTimePicker
                                                label={l10n.getString("control-kitchen-datetime-to-label")}
                                                dateTime={toDateTime}
                                                setDateTime={setToDateTime}
                                                minTime={fromDateTime.plus({ minutes: 15 })}
                                                isMinutesStep={false}
                                            />
                                        </>
                                    )}

                                    {/* HAMZA TODO: Make type commponent */}
                                    {mode === "DELIVERY_TIME" && (
                                        <FormControl className={classes.checkInStatusFilter}>
                                            <LocalizedStrict id="control-kitchen-check-in-type">
                                                <InputLabel id="select-type-label">Type</InputLabel>
                                            </LocalizedStrict>
                                            <Select
                                                value={checkInFilter}
                                                onChange={handleChangeCheckInFilter}
                                                labelId="select-type-label"
                                                label="Type"
                                            >
                                                {[...iterateStringEnum(CheckInFilter)].map((value) => (
                                                    <MenuItem key={value} value={value}>
                                                        <LocalizedEnum
                                                            base="control-kitchen-checked-in-filter"
                                                            enumValue={value}
                                                        >
                                                            <span>{value}</span>
                                                        </LocalizedEnum>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Grid>

                                <Grid
                                    item
                                    container
                                    wrap="nowrap"
                                    width="auto"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                >
                                    <Box sx={{ marginRight: "0.5rem" }}>
                                        <LocalizedStrict
                                            id="control-kitchen-last-updated"
                                            vars={{ lastUpdated: lastUpdatedTime() }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                Last updated: {lastUpdatedTime()}
                                            </Typography>
                                        </LocalizedStrict>
                                        <Tooltip title={l10n.getString("control-kitchen-refresh")}>
                                            <IconButton onClick={() => queryResult.refetch()}>
                                                <FontAwesomeIcon size="xs" icon={faArrowRotateRight} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <DropdownExportButtonControlKitchen
                                        rowsIsSelected={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                                        containsRows={table.getPrePaginationRowModel().rows.length === 0}
                                        allRowsWithFilter={table.getPrePaginationRowModel().rows}
                                        seletecRows={table.getSelectedRowModel().rows}
                                        chosenStartDate={
                                            isPeriodReportMode
                                                ? periodDateRange.startDate.toString()
                                                : fromDateTime.toString()
                                        }
                                        chosenEndDate={
                                            isPeriodReportMode
                                                ? periodDateRange.endDate.toString()
                                                : toDateTime.toString()
                                        }
                                    />
                                    <StandardToggleGlobalFilterButton table={table} />
                                    <MRT_FullScreenToggleButton
                                        table={table}
                                        sx={{ paddingRight: 0, marginRight: "-0.5rem" }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                    isLoading={queryResult.isLoading}
                    isFetching={queryResult.isFetching}
                    isError={queryResult.isError}
                    state={{
                        columnFilters,
                        columnVisibility: {
                            [PURCHASE_TIME_ID]: mode !== "DELIVERY_TIME",
                            [CHECK_IN_ID]: mode !== "PURCHASE_TIME",
                        },
                    }}
                />
            </Grid>
            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={queryResult.data?.orders}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}
        </>
    )
}
